import React, { useState, useEffect, useCallback } from 'react'
import { Zap, Smartphone, Monitor, AlertTriangle, Clock, Activity, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import {
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { fetchAnalytics } from '../../../../api/analyticsClient'
import KPICard from '../components/KPICard'
import AnalyticsCard from '../components/AnalyticsCard'
import styles from './EngagementSection.module.css'

const COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#be185d', '#65a30d']

const fmt = (n) => {
  if (n === null || n === undefined) return '—'
  const num = Number(n)
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toLocaleString('en-IN')
}
const fmtMs = (n) => n !== null && n !== undefined ? `${Math.round(n)}ms` : '—'
const fmtPct = (n) => (n !== null && n !== undefined) ? parseFloat(n).toFixed(1) + '%' : '—'
const initState = () => ({ data: null, loading: true, error: null })

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const HeatmapGrid = ({ days, hours, data }) => {
  const grid = {}
  data.forEach(d => {
    if (!grid[d.day_of_week]) grid[d.day_of_week] = {}
    grid[d.day_of_week][d.hour_of_day] = d.active_users
  })
  const maxVal = Math.max(1, ...data.map(d => d.active_users || 0))

  return (
    <div className={styles.heatmap}>
      <div className={styles.heatmapHeaderRow}>
        <div className={styles.heatmapCorner} />
        {hours.map(h => (
          <div key={h} className={styles.heatmapHourLabel}>{h % 4 === 0 ? `${h}` : ''}</div>
        ))}
      </div>
      {days.map((day, dayIdx) => (
        <div key={day} className={styles.heatmapRow}>
          <div className={styles.heatmapDayLabel}>{day}</div>
          {hours.map(h => {
            const val = grid[dayIdx]?.[h] || 0
            const intensity = val / maxVal
            return (
              <div
                key={h}
                className={styles.heatmapCell}
                style={{ opacity: Math.max(0.07, intensity) }}
                title={`${day} ${h}:00 IST — ${val} active users`}
              />
            )
          })}
        </div>
      ))}
      <div className={styles.heatmapLegend}>
        <span className={styles.heatmapLegendLabel}>Low</span>
        <div className={styles.heatmapLegendBar} />
        <span className={styles.heatmapLegendLabel}>High</span>
      </div>
    </div>
  )
}

const SortableTable = ({ columns, rows, defaultSort }) => {
  const [sortKey, setSortKey] = useState(defaultSort || columns[0]?.key)
  const [sortDir, setSortDir] = useState('desc')

  const sorted = [...(rows || [])].sort((a, b) => {
    const av = a[sortKey]
    const bv = b[sortKey]
    if (typeof av === 'number') return sortDir === 'desc' ? bv - av : av - bv
    return sortDir === 'desc'
      ? String(bv).localeCompare(String(av))
      : String(av).localeCompare(String(bv))
  })

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortKey(key); setSortDir('desc') }
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} className={styles.th} onClick={() => handleSort(col.key)}>
                <span className={styles.thInner}>
                  {col.label}
                  {sortKey === col.key
                    ? sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />
                    : <ChevronsUpDown size={12} className={styles.sortIdle} />}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={i} className={styles.tr}>
              {columns.map(col => (
                <td key={col.key} className={styles.td}>
                  {col.render ? col.render(row[col.key], row) : row[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const EngagementSection = ({ dateRange }) => {
  const [dau, setDau] = useState(initState)
  const [activeTrend, setActiveTrend] = useState(initState)
  const [apiCalls, setApiCalls] = useState(initState)
  const [platform, setPlatform] = useState(initState)
  const [osDistrib, setOsDistrib] = useState(initState)
  const [deviceType, setDeviceType] = useState(initState)
  const [topEndpoints, setTopEndpoints] = useState(initState)
  const [errorRates, setErrorRates] = useState(initState)
  const [responseTimes, setResponseTimes] = useState(initState)
  const [heatmap, setHeatmap] = useState(initState)
  const [granularity, setGranularity] = useState('daily')

  const { from, to } = dateRange

  const loadAll = useCallback(async () => {
    const setState = (setter) => setter(s => ({ ...s, loading: true, error: null }))
    setState(setDau); setState(setActiveTrend); setState(setApiCalls)
    setState(setPlatform); setState(setOsDistrib); setState(setDeviceType)
    setState(setTopEndpoints); setState(setErrorRates); setState(setResponseTimes)
    setState(setHeatmap)

    const [dauRes, trendRes, apiRes, platformRes, osRes, deviceRes, endpointsRes, errorsRes, timesRes, heatmapRes] = await Promise.all([
      fetchAnalytics('/analytics/engagement/dau-wau-mau'),
      fetchAnalytics('/analytics/engagement/active-users-trend', { from, to, granularity }),
      fetchAnalytics('/analytics/engagement/api-calls-per-user', { from, to }),
      fetchAnalytics('/analytics/engagement/platform-split', { from, to }),
      fetchAnalytics('/analytics/engagement/os-distribution', { from, to }),
      fetchAnalytics('/analytics/engagement/device-type', { from, to }),
      fetchAnalytics('/analytics/engagement/top-endpoints', { from, to, limit: 15 }),
      fetchAnalytics('/analytics/engagement/error-rates', { from, to }),
      fetchAnalytics('/analytics/engagement/response-times', { from, to }),
      fetchAnalytics('/analytics/engagement/hourly-heatmap', { from, to }),
    ])

    setDau({ data: dauRes.data, loading: false, error: dauRes.error })
    setActiveTrend({ data: trendRes.data, loading: false, error: trendRes.error })
    setApiCalls({ data: apiRes.data, loading: false, error: apiRes.error })
    setPlatform({ data: platformRes.data, loading: false, error: platformRes.error })
    setOsDistrib({ data: osRes.data, loading: false, error: osRes.error })
    setDeviceType({ data: deviceRes.data, loading: false, error: deviceRes.error })
    setTopEndpoints({ data: endpointsRes.data, loading: false, error: endpointsRes.error })
    setErrorRates({ data: errorsRes.data, loading: false, error: errorsRes.error })
    setResponseTimes({ data: timesRes.data, loading: false, error: timesRes.error })
    setHeatmap({ data: heatmapRes.data, loading: false, error: heatmapRes.error })
  }, [from, to, granularity])

  useEffect(() => { loadAll() }, [loadAll])

  const handleGranularity = async (g) => {
    setGranularity(g)
    setActiveTrend(s => ({ ...s, loading: true, error: null }))
    const res = await fetchAnalytics('/analytics/engagement/active-users-trend', { from, to, granularity: g })
    setActiveTrend({ data: res.data, loading: false, error: res.error })
  }

  const stickinessStatus = () => {
    const v = dau.data?.stickiness
    if (v === null || v === undefined) return undefined
    if (v >= 20) return 'green'
    if (v >= 10) return 'amber'
    return 'red'
  }

  const errorStatus = () => {
    const v = errorRates.data?.summary?.error_rate_pct
    if (v === null || v === undefined) return undefined
    if (v <= 1) return 'green'
    if (v <= 2) return 'amber'
    return 'red'
  }

  return (
    <div className={styles.section}>
      {/* DAU/WAU/MAU/Stickiness */}
      <div className={styles.kpiRow}>
        <KPICard label="DAU" value={fmt(dau.data?.dau)} icon={Activity} subtext="Unique users today" loading={dau.loading} />
        <KPICard label="WAU" value={fmt(dau.data?.wau)} icon={Activity} subtext="Unique users last 7d" loading={dau.loading} />
        <KPICard label="MAU" value={fmt(dau.data?.mau)} icon={Activity} subtext="Unique users last 30d" loading={dau.loading} />
        <KPICard
          label="Stickiness (DAU/MAU)"
          value={fmtPct(dau.data?.stickiness)}
          icon={Zap}
          status={stickinessStatus()}
          subtext={dau.data?.stickiness >= 20 ? 'Healthy' : dau.data?.stickiness >= 10 ? 'Needs attention' : dau.data?.stickiness !== undefined ? 'Low engagement' : undefined}
          loading={dau.loading}
        />
      </div>

      {/* Active Users Trend */}
      <AnalyticsCard
        title="Active Users Trend"
        subtitle="Unique active users over time"
        loading={false}
        error={activeTrend.error}
        actions={
          <div className={styles.toggle}>
            {['daily', 'weekly', 'monthly'].map(g => (
              <button
                key={g}
                className={`${styles.toggleBtn} ${granularity === g ? styles.toggleBtnActive : ''}`}
                onClick={() => handleGranularity(g)}
              >
                {g[0].toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        }
      >
        {activeTrend.loading ? <div className={styles.chartSkeleton} /> : !activeTrend.data?.length ? (
          <div className={styles.empty}>No data for this period</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={activeTrend.data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id="activeUsersGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt} />
              <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }} formatter={v => [fmt(v), 'Active Users']} />
              <Area type="monotone" dataKey="active_users" stroke="#2563eb" strokeWidth={2.5} fill="url(#activeUsersGrad)" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </AnalyticsCard>

      {/* API Calls + Platform + Device */}
      <div className={styles.threeGrid}>
        <AnalyticsCard title="API Calls Per User" subtitle="Engagement depth metrics" loading={apiCalls.loading} error={apiCalls.error}>
          {apiCalls.data && (
            <div className={styles.miniKpiGrid}>
              <div className={styles.miniKpi}>
                <span className={styles.miniKpiValue}>{fmt(apiCalls.data.calls_per_user)}</span>
                <span className={styles.miniKpiLabel}>Calls / User</span>
                <span className={styles.miniKpiSub}>avg over period</span>
              </div>
              <div className={styles.miniKpiDivider} />
              <div className={styles.miniKpi}>
                <span className={styles.miniKpiValue}>{Number(apiCalls.data.calls_per_user_per_day).toFixed(1)}</span>
                <span className={styles.miniKpiLabel}>Calls / User / Day</span>
                <span className={styles.miniKpiSub}>engagement depth</span>
              </div>
            </div>
          )}
          {apiCalls.data && (
            <div className={styles.apiCallsMeta}>
              <span>{fmt(apiCalls.data.total_calls)} total calls</span>
              <span className={styles.dot} />
              <span>{fmt(apiCalls.data.unique_users)} unique users</span>
            </div>
          )}
        </AnalyticsCard>

        <AnalyticsCard title="Platform Split" subtitle="Android vs iOS vs others" loading={platform.loading} error={platform.error}>
          {platform.data?.length > 0 && (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={platform.data} dataKey="unique_users" nameKey="platform" cx="50%" cy="50%" innerRadius={44} outerRadius={68} paddingAngle={2}>
                    {platform.data.map((entry, i) => <Cell key={entry.platform} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => [fmt(v), 'Unique Users']} contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className={styles.platformTable}>
                {platform.data.map((p, i) => (
                  <div key={p.platform} className={styles.platformRow}>
                    <span className={styles.platformDot} style={{ background: COLORS[i % COLORS.length] }} />
                    <span className={styles.platformName}>{p.platform}</span>
                    <span className={styles.platformUsers}>{fmt(p.unique_users)}</span>
                    <span className={styles.platformPct}>{fmtPct(p.user_pct)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </AnalyticsCard>

        <AnalyticsCard title="Device Type" subtitle="Device category breakdown" loading={deviceType.loading} error={deviceType.error}>
          {deviceType.data?.length > 0 && (
            <div className={styles.devicePills}>
              {deviceType.data.map((d, i) => (
                <div key={d.device_type} className={styles.devicePill}>
                  <div className={styles.devicePillHeader}>
                    <span className={styles.devicePillDot} style={{ background: COLORS[i % COLORS.length] }} />
                    <span className={styles.devicePillName}>{d.device_type}</span>
                    <span className={styles.devicePillPct}>{fmtPct(d.pct)}</span>
                  </div>
                  <div className={styles.devicePillBar}>
                    <div className={styles.devicePillBarFill} style={{ width: `${Math.min(100, d.pct || 0)}%`, background: COLORS[i % COLORS.length] }} />
                  </div>
                  <div className={styles.devicePillMeta}>
                    <span>{fmt(d.unique_users)} users</span>
                    <span className={styles.dot} />
                    <span>{fmt(d.total_requests)} requests</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </AnalyticsCard>
      </div>

      {/* OS Version Distribution */}
      <AnalyticsCard
        title="OS Version Distribution"
        subtitle="Top 20 OS versions — use for minimum OS version decisions"
        loading={osDistrib.loading}
        error={osDistrib.error}
      >
        {osDistrib.data?.length > 0 && (
          <ResponsiveContainer width="100%" height={Math.min(400, osDistrib.data.length * 24 + 40)}>
            <BarChart data={osDistrib.data} layout="vertical" margin={{ top: 0, right: 16, left: 110, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt} />
              <YAxis type="category" dataKey="os_version" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={110} />
              <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }} formatter={v => [fmt(v), 'Unique Users']} />
              <Bar dataKey="unique_users" fill="#7c3aed" radius={[0, 4, 4, 0]} maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </AnalyticsCard>

      {/* Top Endpoints */}
      <AnalyticsCard
        title="Top Endpoints"
        subtitle="Feature usage map — which app screens are most used"
        loading={topEndpoints.loading}
        error={topEndpoints.error}
      >
        {topEndpoints.data?.length > 0 && (
          <SortableTable
            defaultSort="total_calls"
            columns={[
              { key: 'endpoint', label: 'Endpoint', render: (v, row) => <span className={styles.endpointCell}><span className={styles.methodBadge} data-method={row.method}>{row.method}</span>{v}</span> },
              { key: 'total_calls', label: 'Total Calls', render: v => fmt(v) },
              { key: 'unique_users', label: 'Unique Users', render: v => fmt(v) },
              { key: 'avg_response_ms', label: 'Avg Response', render: v => <span className={v > 500 ? styles.slow : v > 200 ? styles.medium : styles.fast}>{fmtMs(v)}</span> },
              { key: 'error_count', label: 'Errors', render: v => <span className={v > 0 ? styles.errorCount : ''}>{fmt(v)}</span> },
            ]}
            rows={topEndpoints.data}
          />
        )}
      </AnalyticsCard>

      {/* Error Rates + Response Times */}
      <div className={styles.twoGrid}>
        <AnalyticsCard
          title="Error Rates"
          subtitle="4xx and 5xx error distribution"
          loading={errorRates.loading}
          error={errorRates.error}
        >
          {errorRates.data && (
            <div className={styles.errorContent}>
              <div className={styles.errorKpis}>
                <div className={styles.errorKpi}>
                  <span className={`${styles.errorKpiValue} ${errorStatus() === 'red' ? styles.errorRed : errorStatus() === 'amber' ? styles.errorAmber : styles.errorGreen}`}>
                    {fmtPct(errorRates.data.summary?.error_rate_pct)}
                  </span>
                  <span className={styles.errorKpiLabel}>Error Rate</span>
                </div>
                <div className={styles.errorKpi}>
                  <span className={styles.errorKpiValue}>{fmt(errorRates.data.summary?.client_errors_4xx)}</span>
                  <span className={styles.errorKpiLabel}>4xx Errors</span>
                </div>
                <div className={styles.errorKpi}>
                  <span className={styles.errorKpiValue}>{fmt(errorRates.data.summary?.server_errors_5xx)}</span>
                  <span className={styles.errorKpiLabel}>5xx Errors</span>
                </div>
              </div>
              {errorRates.data.summary?.error_rate_pct > 2 && (
                <div className={styles.errorAlert}>
                  <AlertTriangle size={13} />
                  Error rate exceeds 2% threshold — investigate immediately
                </div>
              )}
              {errorRates.data.top_error_endpoints?.length > 0 && (
                <div className={styles.errorTable}>
                  <div className={styles.errorTableHeader}>Top Error Endpoints</div>
                  {errorRates.data.top_error_endpoints.map((ep, i) => (
                    <div key={i} className={styles.errorTableRow}>
                      <span className={styles.errorEndpoint}>{ep.endpoint}</span>
                      <span className={styles.errorStatus}>{ep.status_code}</span>
                      <span className={styles.errorCountBadge}>{fmt(ep.count)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="Response Times"
          subtitle="Latency percentiles and slowest endpoints"
          loading={responseTimes.loading}
          error={responseTimes.error}
        >
          {responseTimes.data && (
            <div className={styles.rtContent}>
              <div className={styles.rtKpis}>
                {[
                  { label: 'Avg', value: responseTimes.data.summary?.avg_ms },
                  { label: 'P50', value: responseTimes.data.summary?.p50_ms },
                  { label: 'P95', value: responseTimes.data.summary?.p95_ms },
                  { label: 'P99', value: responseTimes.data.summary?.p99_ms },
                ].map(({ label, value }) => (
                  <div key={label} className={styles.rtKpi}>
                    <span className={`${styles.rtKpiValue} ${value > 1000 ? styles.slow : value > 500 ? styles.medium : styles.fast}`}>{fmtMs(value)}</span>
                    <span className={styles.rtKpiLabel}>{label}</span>
                  </div>
                ))}
              </div>
              {responseTimes.data.by_endpoint?.length > 0 && (
                <div className={styles.errorTable}>
                  <div className={styles.errorTableHeader}>Slowest Endpoints</div>
                  {responseTimes.data.by_endpoint.map((ep, i) => (
                    <div key={i} className={styles.errorTableRow}>
                      <span className={styles.errorEndpoint}>{ep.endpoint}</span>
                      <span className={`${styles.errorCountBadge} ${ep.avg_ms > 1000 ? styles.slow : ep.avg_ms > 500 ? styles.medium : styles.fast}`}>{fmtMs(ep.avg_ms)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </AnalyticsCard>
      </div>

      {/* Hourly Heatmap */}
      <AnalyticsCard
        title="Activity Heatmap"
        subtitle="Active users by hour of day and day of week (IST)"
        loading={heatmap.loading}
        error={heatmap.error}
        minHeight={220}
      >
        {heatmap.data?.data?.length > 0 && (
          <HeatmapGrid
            days={heatmap.data.days || DAYS}
            hours={heatmap.data.hours || Array.from({ length: 24 }, (_, i) => i)}
            data={heatmap.data.data}
          />
        )}
      </AnalyticsCard>
    </div>
  )
}

export default EngagementSection
